import { useInvestmentAccountDetail } from '@egw/api'
import { Button, ButtonVariant, Collapse, ModalDrawer, PageHeaderRedesign, useModalState } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { RecentInvestmentActivity } from '@egw/features-transactions'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate, useParams } from 'react-router'
import { ApexHoldings, CompanionHolding, InvestmentOverflowMenu } from '../../../components'
import { type Config } from '../../../config/config'

export const InvestmentsAccountRedesignPage: FC = () => {
  const config = configClient.getConfig() as Config
  const { isOpen: overflowMenuOpen, openModal: openOverflowMenu, closeModal: closeOverflowMenu } = useModalState()
  const { t } = useTranslation('features-investment')
  const navigate = useNavigate()
  const { alias = '' } = useParams<{ alias: string }>()

  const {
    data: investmentAccountDetail,
    isLoading: isInvestmentAccountDetailLoading,
    isError: isInvestmentAccountDetailError,
    dataUpdatedAt,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: alias !== '',
  })

  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  const isLoading = isInvestmentAccountDetailLoading
  const isError = isInvestmentAccountDetailError

  const accountValue = investmentAccountDetail?.balance.accountValue
  const investedBalance = investmentAccountDetail?.balance.investedBalance
  const cashBalance = investmentAccountDetail?.balance.cashBalance
  const returnSinceInception = investmentAccountDetail?.balance.amountSinceInception

  const isCompanionAccount = Boolean(
    investmentAccountDetail?.__typename === 'InvestmentBrokerageCompanionAccount' ||
      investmentAccountDetail?.__typename === 'InvestmentIRACompanionAccount' ||
      investmentAccountDetail?.__typename === 'InvestmentRothIRACompanionAccount' ||
      investmentAccountDetail?.__typename === 'InvestmentJointBrokerageTenantsInCommonCompanionAccount' ||
      investmentAccountDetail?.__typename === 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount',
  )

  return (
    <>
      {isLoading ? (
        <div className="p-xs bg-primary-500 typo-head-1 egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {isError ? (
        <div className="egw-container !pt-lg">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('activity.error')}</p>
        </div>
      ) : null}

      {investmentAccountDetail && !isLoading ? (
        <>
          <PageHeaderRedesign
            subtitle={t(`account_type.${investmentAccountDetail.__typename}`).toUpperCase()}
            title={investmentAccountDetail.accountName}
            backLinkUrl={WEALTH_ROUTES.INVESTMENTS_OVERVIEW.path}
            lastRefreshed={lastRefreshed}
          />
          <div className="egw-container">
            <div className="overview-card mb-md">
              <h3 className="typo-primary-sm-light text-text-header">{t('investment.account_value')}</h3>

              <Collapse
                className="items-baseline"
                chevronClassName="text-icons-muted"
                title={
                  <p className="typo-primary-2xl-regular text-text-header">
                    {accountValue ? <span>{getCurrencySymbol(accountValue.currencyCode)}</span> : null}
                    <span>
                      {accountValue?.amount
                        ? Currency.integerCents.onlyMinus(accountValue.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </p>
                }
              >
                <div className="flex typo-primary-base-regular text-text-meta mb-2xs">
                  <p className="flex-1">{t('investment.invested_balance')}</p>
                  <div>
                    {investedBalance ? <span>{getCurrencySymbol(investedBalance.currencyCode)}</span> : null}
                    <span>
                      {investedBalance?.amount
                        ? Currency.integerCents.onlyMinus(investedBalance.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </div>
                </div>
                <div className="flex typo-primary-base-regular text-text-meta mb-md">
                  <p className="flex-1">{t('investment.cash_balance')}</p>
                  <div>
                    {cashBalance ? <span>{getCurrencySymbol(cashBalance.currencyCode)}</span> : null}
                    <span>
                      {cashBalance?.amount
                        ? Currency.integerCents.onlyMinus(cashBalance.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </div>
                </div>
              </Collapse>

              <hr className="mb-md" />

              {returnSinceInception ? (
                <div className="flex typo-primary-base-regular text-text-meta flex flex-row justify-between">
                  <div className="flex flex-row gap-1 items-center">
                    <p className="flex-1">{t('investment.return_since_inception')}</p>
                  </div>
                  <p>
                    <span>{getCurrencySymbol(returnSinceInception.currencyCode)}</span>
                    <span>
                      {returnSinceInception.amount
                        ? Currency.integerCents.onlyMinus(returnSinceInception.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </p>
                </div>
              ) : null}
            </div>

            <div className="flex gap-sm mb-xl">
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  className="w-full py-sm"
                  testId="investment-account-transfer-button"
                  onClick={() => {
                    sendEvent('user_brokerageTranser_click', {
                      eventSource: 'egWealth',
                      category: 'transfer',
                      subCategory: 'intent',
                    })
                    void navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                  isDisabled={config.env === 'demo-dev' || config.env === 'demo-prod'}
                >
                  {t('investment.cta_transfer')}
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  className="w-full py-4"
                  testId="investment-account-details-button"
                  onClick={openOverflowMenu}
                >
                  {t('investment.cta_account_info')}
                </Button>
              </div>
            </div>

            {isCompanionAccount ? (
              <div className="mb-2xl">
                {'holdings' in investmentAccountDetail ? (
                  <div className="typo-primary-xl-light text-text-header flex flex-col">
                    {investmentAccountDetail.holdings.length ? (
                      <>
                        <div className="my-sm">
                          {investmentAccountDetail.holdings
                            .sort((a, b) => b.amount.amount - a.amount.amount)
                            .slice(0, 3)
                            .map((holding) => (
                              <CompanionHolding
                                href={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS_DETAILS.path
                                  .replace(':alias', alias.toString())
                                  .replace(':symbol', holding.symbol)}
                                key={holding.symbol}
                                {...holding}
                              />
                            ))}
                        </div>
                        {investmentAccountDetail.holdings.length > 3 ? (
                          <Button
                            variant={ButtonVariant.Secondary}
                            className="w-full md:w-fit"
                            testId="investment-account-all-holdings-button"
                            onClick={() => {
                              void navigate(
                                WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS.path.replace(
                                  ':alias',
                                  alias.toString(),
                                ),
                              )
                            }}
                          >
                            {t('companion_account.all_holdings')}
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <div className="flex flex-col items-center mt-2xl">
                        <p className="typo-primary-xl-light text-text-header">{t('holdings.no_holdings')}</p>
                        <p className="typo-primary-base text-text-body">{t('holdings.any_holdings')}</p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                {/* Holdings Section */}
                {'assetAllocation' in investmentAccountDetail ? (
                  <div className="mb-2xl">
                    <ApexHoldings alias={parseInt(alias)} data={investmentAccountDetail.assetAllocation} />
                  </div>
                ) : null}
              </>
            )}

            <ModalDrawer
              title={t('account_options.account_info')}
              isOpen={overflowMenuOpen}
              onClose={closeOverflowMenu}
            >
              <InvestmentOverflowMenu alias={alias} />
            </ModalDrawer>
          </div>
        </>
      ) : null}

      {/* Recent Investment Activity */}
      {investmentAccountDetail?.alias !== undefined && !isLoading ? (
        <RecentInvestmentActivity alias={investmentAccountDetail.alias} />
      ) : null}
    </>
  )
}
